// src/utils/abonnements.ts — SCE Issues
// Cloche « Suivre un problème » (Lot 12) : lecture de la colonne Abonnes,
// appartenance, bascule. Pur, testable hors ligne.
//
// ─── La colonne ───
//
// Abonnes (SCE_Problemes) : Personne ou groupe, plusieurs valeurs, créée le
// 25/09. Même mécanique que la colonne Coordinateur : on ÉCRIT des
// identifiants SharePoint (`AbonnesLookupId`, obtenus par ensureUser).
//
// En LECTURE, Graph renvoie une colonne Personne multiple sous forme de
// tableau d'objets `{ LookupId, LookupValue, Email }` ; selon la requête, on
// peut aussi trouver `AbonnesLookupId` (tableau d'identifiants). Les deux
// formes sont acceptées : un format inattendu donne « personne ne suit »,
// jamais une erreur qui bloquerait la fiche.
//
// ─── Qui est « moi » ───
//
// Par l'identifiant SharePoint quand on le connaît (ensureUser, en cache),
// sinon par l'adresse. Les deux sont essayés : l'identifiant est sûr, l'adresse
// est disponible tout de suite.

import { estActif, type Abonne, type Issue } from '@/types/issue.types'
import { AUCUN_ELEMENT_SUIVI, motifSuivi, type ElementsSuivis } from './elementsSuivis'
import { nomComplet } from '@/utils/noms'

function versNombre(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN
  return Number.isInteger(n) && n > 0 ? n : null
}

/**
 * Colonne Abonnes (et/ou AbonnesLookupId) telle que Graph la renvoie →
 * abonnés, sans doublon, dans l'ordre reçu.
 */
export function lireAbonnes(brut: unknown, brutIds?: unknown): Abonne[] {
  const vus = new Map<number, Abonne>()
  if (Array.isArray(brut)) {
    for (const v of brut) {
      if (!v || typeof v !== 'object') continue
      const o = v as Record<string, unknown>
      const id = versNombre(o['LookupId'])
      if (id === null || vus.has(id)) continue
      const email = typeof o['Email'] === 'string' && o['Email'].trim() ? o['Email'].trim() : undefined
      const nom = typeof o['LookupValue'] === 'string' && o['LookupValue'].trim() ? nomComplet(o['LookupValue'], email) : undefined
      vus.set(id, { lookupId: id, ...(nom ? { nom } : {}), ...(email ? { email } : {}) })
    }
  }
  const ids = Array.isArray(brutIds) ? brutIds : brutIds !== undefined && brutIds !== null ? [brutIds] : []
  for (const v of ids) {
    const id = versNombre(v)
    if (id !== null && !vus.has(id)) vus.set(id, { lookupId: id })
  }
  return [...vus.values()]
}

/** La personne (identifiant SharePoint et/ou adresse) suit-elle ce problème ? */
export function estAbonne(abonnes: readonly Abonne[], monId: number | null, upn: string): boolean {
  const cle = upn.trim().toLowerCase()
  return abonnes.some(
    (a) => (monId !== null && a.lookupId === monId) || (!!cle && a.email?.toLowerCase() === cle),
  )
}

/**
 * Identifiants à écrire après « suivre » / « ne plus suivre ». Part de la
 * liste RELUE juste avant l'écriture : un abonnement posé entre-temps par
 * quelqu'un d'autre est conservé.
 */
export function identifiantsApres(actuels: readonly Abonne[], monId: number, suivre: boolean): number[] {
  const ids = actuels.map((a) => a.lookupId).filter((id) => id !== monId)
  return suivre ? [...ids, monId] : ids
}

/** « Suivi par vous et 2 autres personnes », « Suivi par 3 personnes »… */
export function libelleSuivi(total: number, moiInclus: boolean): string {
  if (total === 0) return 'Personne ne suit ce problème'
  if (moiInclus) {
    const autres = total - 1
    if (autres === 0) return 'Suivi par vous'
    return `Suivi par vous et ${autres} autre${autres > 1 ? 's' : ''} personne${autres > 1 ? 's' : ''}`
  }
  return `Suivi par ${total} personne${total > 1 ? 's' : ''}`
}

/**
 * Les problèmes suivis, pour l'accueil : ceux qu'on suit un par un, plus les
 * problèmes ACTIFS des équipements (Lot 12 ter) et des projets (Lot 12 bis)
 * suivis. Les actifs d'abord, du plus récemment bougé au plus ancien
 * (dernière activité si connue, sinon déclaration), puis les terminés suivis
 * un par un, qu'on peut encore vouloir retrouver.
 */
export function problemesSuivis(
  issues: readonly Issue[],
  monId: number | null,
  upn: string,
  activites?: ReadonlyMap<string, number>,
  elements: ElementsSuivis = AUCUN_ELEMENT_SUIVI,
): Issue[] {
  const quand = (i: Issue): number => activites?.get(i.id) ?? (Date.parse(i.dateDeclaration) || 0)
  return issues
    .filter((i) => motifSuivi(i, estAbonne(i.abonnes, monId, upn), elements) !== null)
    .sort((a, b) => Number(estActif(b)) - Number(estActif(a)) || quand(b) - quand(a))
}

// ─── Abonnement automatique à ses déclarations (29/10) ───
//
// À la déclaration, le déclarant est abonné d'office à son problème (et aux
// fiches sœurs d'un défaut sur plusieurs équipements). Réglage « M'abonner aux
// problèmes que je déclare » de la page Mes notifications, bloc `abonnements`
// du JSON Prefs de SA ligne de SCE_UserPrefs : absent = actif.

export const CLE_BLOC_ABONNEMENTS = 'abonnements'

export interface ReglageAbonnements {
  version: 1
  /** Abonné d'office aux problèmes qu'on déclare. */
  mesDeclarations: boolean
}

/** Lecture tolérante : tout ce qui n'est pas un « non » explicite vaut « oui ». */
export function lireReglageAbonnements(bloc: unknown): ReglageAbonnements {
  const non = !!bloc && typeof bloc === 'object' && (bloc as { mesDeclarations?: unknown }).mesDeclarations === false
  return { version: 1, mesDeclarations: !non }
}

/**
 * Les problèmes suivis un par un (cloche de la fiche, ou abonnement à la
 * déclaration) : actifs d'abord, puis du plus récent au plus ancien.
 */
export function problemesAbonnes(issues: readonly Issue[], monId: number | null, upn: string): Issue[] {
  return issues
    .filter((i) => estAbonne(i.abonnes, monId, upn))
    .sort((a, b) => Number(estActif(b)) - Number(estActif(a)) || (Date.parse(b.dateDeclaration) || 0) - (Date.parse(a.dateDeclaration) || 0))
}
